import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, Button, Table } from 'react-daisyui';
import TopNavbar from './TopNavbar';
import Sidebar from './Sidebar';

const API_BASE_URL = 'https://berachain-v2-blacknodes-el-api.blacknodes.net/api';

const InfoRow = ({ label, value, icon, copyable }) => (
  <div className="flex items-center py-2 border-b border-gray-700">
    <div className="w-1/3 flex items-center">
      {icon && <span className="mr-2">{icon}</span>}
      <span className="text-gray-400">{label}</span>
    </div>
    <div className="w-2/3 break-all flex items-center justify-between">
      <span>{value}</span>
      {copyable && (
        <Button size="sm" color="ghost" onClick={() => navigator.clipboard.writeText(value)}>
          <i className="fas fa-copy"></i>
        </Button>
      )}
    </div>
  </div>
);

const formatTokenAmount = (amount, decimals = 18) => {
  const parsedAmount = parseFloat(amount) / Math.pow(10, decimals);
  return parsedAmount.toLocaleString(undefined, { maximumFractionDigits: 18 });
};

const AddressDetailsPage = () => {
  const { address } = useParams();
  const [addressDetails, setAddressDetails] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [transactionCount, setTransactionCount] = useState(0);
  const [transferCount, setTransferCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAddressData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [balanceResponse, tokenResponse, txCountResponse, transferCountResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}`, {
            params: {
              module: 'account',
              action: 'balance',
              address: address
            }
          }),
          axios.get(`${API_BASE_URL}`, {
            params: {
              module: 'account',
              action: 'tokenlist',
              address: address
            }
          }),
          axios.get(`${API_BASE_URL}`, {
            params: {
              module: 'account',
              action: 'txlist',
              address: address
            }
          }),
          axios.get(`${API_BASE_URL}`, {
            params: {
              module: 'account',
              action: 'tokentx',
              address: address
            }
          })
        ]);

        setAddressDetails({
          balance: formatTokenAmount(balanceResponse.data.result),
          // Add other details as needed
        });
        setTokens(tokenResponse.data.result || []);
        setTransactionCount(txCountResponse.data.result.length);
        setTransferCount(transferCountResponse.data.result.length);
      } catch (err) {
        setError('An error occurred while fetching the address data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAddressData();
  }, [address]);

  const renderAddressDetails = () => {
    if (!addressDetails) return null;

    return (
      <Card className="bg-gray-900 border border-gray-800 mb-4 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
        <Card.Body>
          <div className="flex justify-between items-center mb-4">
            <Card.Title tag="h2" className="text-2xl">Address Details</Card.Title>
            <div>
              <Button size="sm" color="ghost" className="mr-2" onClick={() => navigator.clipboard.writeText(address)}>
                <i className="fas fa-copy"></i>
              </Button>
              <Button size="sm" color="ghost">
                <i className="fas fa-qrcode"></i>
              </Button>
            </div>
          </div>
          <p className="text-gray-400 mb-4">{address}</p>
          <InfoRow
            label="Balance"
            value={`${addressDetails.balance} BERA`}
            icon={<i className="fas fa-coins text-gray-400"></i>}
          />
          <InfoRow
            label="Tokens"
            value={
              <div className="flex items-center">
                <span>{tokens.length} tokens</span>
                <span className="ml-2 text-gray-400">($0.00 USD)</span>
                <Button size="sm" color="ghost" className="ml-2">
                  <i className="fas fa-list"></i>
                </Button>
              </div>
            }
            icon={<i className="fas fa-coins text-gray-400"></i>}
          />
          <InfoRow
            label="Transactions"
            value={
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded">
                {transactionCount} Transactions
              </span>
            }
            icon={<i className="fas fa-exchange-alt text-gray-400"></i>}
          />
          <InfoRow
            label="Transfers"
            value={
              <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded">
                {transferCount} Transfers
              </span>
            }
            icon={<i className="fas fa-exchange-alt text-gray-400"></i>}
          />
          <InfoRow
            label="Gas Used"
            value="7,708,431"
            icon={<i className="fas fa-gas-pump text-gray-400"></i>}
          />
          <InfoRow
            label="Last Balance Update"
            value="5601399"
            icon={<i className="fas fa-clock text-gray-400"></i>}
          />
        </Card.Body>
      </Card>
    );
  };

  const renderTokens = () => {
    if (tokens.length === 0) return null;

    return (
      <Card className="bg-gray-900 border border-gray-800 mb-4 shadow-[0px_20px_207px_10px_rgba(38,_126,_218,_0.93)]">
        <Card.Body>
          <Card.Title tag="h2" className="text-2xl mb-4">Tokens</Card.Title>
          <div className="overflow-x-auto">
            <Table className="w-full">
              <Table.Head>
                <span>Asset</span>
                <span>Type</span>
                <span>Amount</span>
                <span>Symbol</span>
                <span>Price</span>
                <span>Value</span>
                <span>Contract Address</span>
              </Table.Head>
              <Table.Body>
                {tokens.map((token, index) => (
                  <Table.Row key={index}>
                    <span className="text-purple-400">{token.name}</span>
                    <span>{token.type}</span>
                    <span>{formatTokenAmount(token.balance, token.decimals)}</span>
                    <span>{token.symbol}</span>
                    <span>-</span>
                    <span>-</span>
                    <span className="text-purple-400">
                      {token.contractAddress} ({token.contractAddress.slice(0, 6)}...{token.contractAddress.slice(-4)})
                    </span>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <Sidebar />
      <div className="flex-1 p-4 pl-20">
        <TopNavbar />
        <div className="mb-4">
          <h1 className="text-3xl font-bold mb-8">Address Details</h1>
          {loading ? (
            <div className="text-center mt-8">Loading...</div>
          ) : error ? (
            <div className="text-center mt-8 text-red-500">{error}</div>
          ) : (
            <>
              {renderAddressDetails()}
              {renderTokens()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressDetailsPage;